import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useLang } from "@/context/LangContext";

export default function ExhibitorPaymentSuccess() {
  const { t } = useLang();
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const exhId = sp.get("exh_id");

  const [status, setStatus] = useState("polling"); // polling | paid | timeout | error
  const [attempts, setAttempts] = useState(0);
  const [eventId, setEventId] = useState(null);

  useEffect(() => {
    if (!sessionId || !exhId) { setStatus("error"); return; }
    let cancelled = false;
    let attempt = 0;

    const poll = async () => {
      if (cancelled) return;
      attempt += 1;
      setAttempts(attempt);
      try {
        // We need event_id from slug to call the polling endpoint
        if (!eventId) {
          const { data } = await api.get(`/events/${slug}`);
          if (cancelled) return;
          setEventId(data.id);
          setTimeout(poll, 500);
          return;
        }
        const { data } = await api.get(`/events/${eventId}/exhibitors/${exhId}/payment-status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") {
          setStatus("error");
          return;
        }
      } catch (e) { /* keep polling */ }
      if (attempt >= 10) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, exhId, slug, eventId]);

  return (
    <PublicLayout>
      <section className="mx-auto max-w-2xl px-6 py-20">
        <div className="rounded-3xl border border-wine-100 bg-white p-10 text-center shadow-sm" data-testid="exh-payment-success">
          {status === "polling" && (
            <>
              <Loader2 className="mx-auto h-12 w-12 animate-spin text-wine-500" />
              <h1 className="mt-6 font-serif text-3xl text-navy">{t("comp_exhibitorpaymentsuccess_90")}</h1>
              <p className="mt-2 text-sm text-neutral-600">Tentative {attempts}/10 — merci de patienter.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
              <h1 className="mt-6 font-serif text-3xl text-navy">{t("comp_exhibitorpaymentsuccess_91")}</h1>
              <p className="mt-3 text-sm text-neutral-600">{t("comp_exhibitorpaymentsuccess_92")}</p>
              <div className="mt-4 rounded-2xl border border-wine-100 bg-wine-50/40 p-4 text-left text-sm text-neutral-700">
                <strong>{t("comp_exhibitorpaymentsuccess_93")}</strong> {t("comp_exhibitorpaymentsuccess_94")} <Link to={`/portal/event/${slug}/overview`} className="font-bold text-wine-700 hover:underline">{t("comp_exhibitorpaymentsuccess_95")}</Link> {t("comp_exhibitorpaymentsuccess_96")} <strong>{t("comp_exhibitorpaymentsuccess_97")}</strong>. Les visiteurs pourront ensuite réserver un RDV 1:1 avec vous.
              </div>
              <div className="mt-6 flex justify-center gap-3">
                <Link to={`/portal/event/${slug}/overview`} className="rounded-full bg-wine-500 px-6 py-2 text-xs font-bold text-white hover:bg-wine-600" data-testid="exh-go-meetings">{t("comp_exhibitorpaymentsuccess_98")}</Link>
                <Link to={`/events/${slug}`} className="rounded-full border border-neutral-200 bg-white px-6 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-50">{t("comp_exhibitorpaymentsuccess_99")}</Link>
              </div>
            </>
          )}
          {(status === "error" || status === "timeout") && (
            <>
              <XCircle className="mx-auto h-14 w-14 text-rose-500" />
              <h1 className="mt-6 font-serif text-3xl text-navy">{t("comp_exhibitorpaymentsuccess_100")}</h1>
              <p className="mt-3 text-sm text-neutral-600">{t("comp_exhibitorpaymentsuccess_101")}</p>
              <Link to={`/events/${slug}`} className="mt-6 inline-block rounded-full bg-wine-500 px-6 py-2 text-xs font-bold text-white hover:bg-wine-600">{t("comp_exhibitorpaymentsuccess_99")}</Link>
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
